import { useEffect, useMemo, useState } from "react";
import AdminListToolkit from "../components/listing/AdminListToolkit";
import DataTable, { Column } from "../components/DataTable";
import type { FormField } from "../components/AdminDataModal";
import AdminDataModal from "../components/AdminDataModal";
import { useMessageModal } from "../contexts/MessageModalContext";
import "../styles/AdminTestimonials.css";

type JobStatus = "active" | "inactive";
type ModalMode = "add" | "edit" | "view";

interface CareerJobRow {
  id: number;
  job_title: string;
  image: any;
  paragraph_1: string;
  paragraph_2: string;
  bullet_1: string;
  bullet_2: string;
  bullet_3: string;
  posting_date: string;
  last_date: string;
  sort_order: number;
  status: JobStatus;
  createdAt: string;
  updatedAt: string;
}

const BASE_URL = import.meta.env.VITE_LYRAZONE_BASE_URL;

export default function AdminCareerP1() {
  const { showModal } = useMessageModal();

  const [data, setData] = useState<CareerJobRow[]>([]);
  const [selectedRow, setSelectedRow] = useState<CareerJobRow | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<ModalMode>("add");
  const [activeRow, setActiveRow] = useState<any>(null);

  const formatDateForDisplay = (dateString: string) => {
    if (!dateString) return "";

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  };

  const formatDateForInput = (dateString: string) => {
    if (!dateString) return "";

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const fields: FormField[] = useMemo(
    () => [
      { key: "id", label: "ID", colSpan: 2 },
      { key: "job_title", label: "Job Title", type: "text", colSpan: 2 },
      {
        key: "image",
        label: "Image",
        type: "image",
        imageType: "single",
        imageFolder: "career",
        uploadField: "careerImage",
        colSpan: 1,
      },
      {
        key: "paragraph_1",
        label: "Job Description",
        type: "textarea",
        colSpan: 2,
      },
      {
        key: "paragraph_2",
        label: "Additional Details",
        type: "textarea",
        colSpan: 2,
      },
      { key: "bullet_1", label: "Bullet 1", type: "text", colSpan: 2 },
      { key: "bullet_2", label: "Bullet 2", type: "text", colSpan: 2 },
      { key: "bullet_3", label: "Bullet 3", type: "text", colSpan: 2 },
      {
        key: "posting_date",
        label: "Job Posting Date",
        type: "date",
        colSpan: 1,
      },
      {
        key: "last_date",
        label: "Last Date",
        type: "date",
        colSpan: 1,
      },
      {
        key: "sort_order",
        label: "Sort Order",
        type: "number",
        colSpan: 1,
      },
      {
        key: "status",
        label: "Status",
        type: "select",
        options: [
          { label: "Active", value: "active" },
          { label: "Inactive", value: "inactive" },
        ],
        colSpan: 2,
      },
    ],
    [],
  );

  const columns: Column[] = [
    { key: "id", label: "ID" },
    { key: "job_title", label: "Job Title" },
    { key: "image", label: "Image", imageFolder: "career" },
    {
      key: "paragraph_1",
      label: "Job Description",
      render: (row) => (
        <span className="admin-testimonial-text-clamp">{row.paragraph_1}</span>
      ),
    },
    {
      key: "posting_date",
      label: "Posting Date",
      render: (row) =>
        row.posting_date ? formatDateForInput(row.posting_date) : "-",
    },
    {
      key: "last_date",
      label: "Last Date",
      render: (row) =>
        row.last_date ? formatDateForInput(row.last_date) : "-",
    },
    {
      key: "status",
      label: "Status",
      render: (row) => (
        <span
          className={row.status === "active" ? "badge-success" : "badge-danger"}
        >
          {row.status}
        </span>
      ),
    },
  ];

  const fetchJobs = async () => {
    try {
      const res = await fetch(`${BASE_URL}/admin/career-jobs`, {
        credentials: "include",
      });
      const result = await res.json();
      const freshData = result.data || [];

      if (result.success) {
        setData(freshData);
        setSelectedRow((prev: any) =>
          prev
            ? freshData.find((item: any) => item.id === prev.id) || null
            : null,
        );
        setActiveRow((prev: any) =>
          prev
            ? freshData.find((item: any) => item.id === prev.id) || null
            : null,
        );
      }
    } catch (error) {
      console.error("Fetch Career Jobs Error:", error);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, []);

  const buildFormData = (formData: any, fields: FormField[]) => {
    const form = new FormData();

    fields.forEach((field) => {
      const value = formData[field.key];

      if (field.type === "image") {
        if (value?.file) {
          form.append(field.uploadField || field.key, value.file);
        } else if (value?.removed || value === "") {
          form.append(field.key, "REMOVE");
        }
      } else {
        form.append(field.key, value ?? "");
      }
    });

    return form;
  };

  const handleSubmit = async (formData: any) => {
    try {
      const form = buildFormData(formData, fields);

      const method = modalMode === "add" ? "POST" : "PUT";
      const url =
        modalMode === "add"
          ? `${BASE_URL}/admin/career-jobs`
          : `${BASE_URL}/admin/career-jobs/${activeRow.id}`;

      const res = await fetch(url, {
        method,
        credentials: "include",
        body: form,
      });

      const result = await res.json();

      if (result.success) {
        fetchJobs();
        setModalOpen(false);
        setActiveRow(null);

        showModal({
          type: "success",
          title: "Success",
          message:
            modalMode === "add"
              ? "Career job added successfully."
              : "Career job updated successfully.",
          confirmText: "OK",
        });
      } else {
        showModal({
          type: "error",
          title: "Error",
          message: result.message || "Failed to save career job.",
          confirmText: "OK",
        });
      }
    } catch (error) {
      console.error("Submit Career Job Error:", error);
      showModal({
        type: "error",
        title: "Error",
        message: "Something went wrong while saving the career job.",
        confirmText: "OK",
      });
    }
  };

  const handleDelete = (row: any) => {
    showModal({
      type: "confirm",
      title: "Delete Career Job",
      message: `Are you sure you want to delete "${row.job_title}"?`,
      confirmText: "Delete",
      cancelText: "Cancel",
      onConfirm: async () => {
        const res = await fetch(`${BASE_URL}/admin/career-jobs/${row.id}`, {
          method: "DELETE",
          credentials: "include",
        });

        const result = await res.json();

        if (result.success) {
          fetchJobs();
          setSelectedRow(null);
          showModal({
            type: "success",
            title: "Deleted",
            message: "Career job deleted successfully.",
            confirmText: "OK",
          });
        }
      },
    });
  };

  return (
    <div>
      <AdminListToolkit
        title="Career Jobs"
        columns={columns}
        data={data}
        selectedRow={selectedRow}
        onAdd={() => {
          setModalMode("add");
          setActiveRow(null);
          setModalOpen(true);
        }}
        onView={(row) => {
          setModalMode("view");
          const formattedRow = {
            ...row,
            posting_date: formatDateForDisplay(row.posting_date),
            last_date: formatDateForDisplay(row.last_date),
          };

          setActiveRow(formattedRow);
          setModalOpen(true);
        }}
        onEdit={(row) => {
          setModalMode("edit");
          const formattedRow = {
            ...row,
            posting_date: formatDateForInput(row.posting_date),
            last_date: formatDateForInput(row.last_date),
          };

          setActiveRow(formattedRow);
          setModalOpen(true);
        }}
        onSoftDelete={handleDelete}
        enableExport={true}
        exportFileName="career-jobs"
      >
        {(filteredData) => (
          <DataTable
            columns={columns}
            data={filteredData}
            selectedRowId={selectedRow?.id}
            onRowSelect={setSelectedRow}
          />
        )}
      </AdminListToolkit>

      <AdminDataModal
        isOpen={modalOpen}
        mode={modalMode}
        title="Career Job"
        fields={fields}
        rowData={activeRow}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmit}
      />
    </div>
  );
}
